import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { Eye, Users, Clock, MousePointer, Globe, Monitor, Smartphone, Tablet, ArrowRight, TrendingUp, Zap, Loader2, Activity } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#D4AF37', '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-[#1a1f2e] border border-white/10 rounded-lg px-3 py-2 shadow-xl">
            <p className="text-xs text-white/50 mb-1">{label}</p>
            {payload.map((p, i) => (
                <p key={i} className="text-sm font-medium" style={{ color: p.color }}>{p.name}: {p.value}</p>
            ))}
        </div>
    );
};

const DEVICE_ICONS = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

export default function AdminAnalytics() {
    const [data, setData] = useState(null);
    const [realtime, setRealtime] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('30');

    const fetchData = useCallback(async () => {
        try {
            const [overview, rt] = await Promise.all([
                axios.get(`${API}/api/admin/analytics/overview?days=${period}`, { withCredentials: true }),
                axios.get(`${API}/api/admin/analytics/realtime`, { withCredentials: true })
            ]);
            setData(overview.data);
            setRealtime(rt.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [period]);

    useEffect(() => { fetchData(); }, [fetchData]);
    useEffect(() => { const i = setInterval(() => { axios.get(`${API}/api/admin/analytics/realtime`, { withCredentials: true }).then(r => setRealtime(r.data)).catch(() => {}); }, 30000); return () => clearInterval(i); }, []);

    if (loading) return <DashboardLayout type="admin"><div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div></DashboardLayout>;

    const s = data?.summary || {};
    const viewChange = s.yesterday_pageviews > 0 ? Math.round(((s.today_pageviews - s.yesterday_pageviews) / s.yesterday_pageviews) * 100) : 0;

    return (
        <DashboardLayout type="admin">
            <div className="space-y-6" data-testid="admin-analytics">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white">Platform Analytics</h1>
                        <p className="text-sm text-muted-foreground mt-1">Comprehensive visitor insights and behavior analysis</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {realtime && (
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                                <span className="text-xs text-emerald-400 font-medium">{realtime.active_users} active now</span>
                            </div>
                        )}
                        <Select value={period} onValueChange={v => { setPeriod(v); setLoading(true); }}>
                            <SelectTrigger className="w-[130px] bg-white/5 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="7">Last 7 days</SelectItem>
                                <SelectItem value="30">Last 30 days</SelectItem>
                                <SelectItem value="90">Last 90 days</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { label: 'Total Pageviews', value: s.total_pageviews || 0, icon: Eye, sub: `Today: ${s.today_pageviews || 0}`, color: 'text-gold', change: viewChange },
                        { label: 'Unique Visitors', value: s.unique_visitors || 0, icon: Users, sub: `${s.total_sessions || 0} sessions`, color: 'text-blue-400' },
                        { label: 'Avg. Pages/Session', value: s.avg_pages_per_session || 0, icon: MousePointer, sub: 'pages per visit', color: 'text-emerald-400' },
                        { label: 'Avg. Time on Page', value: `${Math.floor((s.avg_time_on_page || 0) / 60)}m ${Math.round((s.avg_time_on_page || 0) % 60)}s`, icon: Clock, sub: 'per page', color: 'text-purple-400' },
                    ].map(kpi => (
                        <Card key={kpi.label} className="bg-[#121620] border-white/10">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[10px] text-white/40 uppercase tracking-wider">{kpi.label}</span>
                                    <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
                                </div>
                                <p className="text-2xl font-bold text-white">{typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}</p>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="text-[10px] text-white/30">{kpi.sub}</span>
                                    {kpi.change !== undefined && kpi.change !== 0 && (
                                        <span className={`text-[10px] font-medium ${kpi.change > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {kpi.change > 0 ? '+' : ''}{kpi.change}% vs yesterday
                                        </span>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Charts Row 1 */}
                <div className="grid lg:grid-cols-3 gap-5">
                    <Card className="lg:col-span-2 bg-[#121620] border-white/10">
                        <CardHeader className="pb-2"><CardTitle className="text-white font-outfit text-base">Pageviews Over Time</CardTitle></CardHeader>
                        <CardContent>
                            <div className="h-[280px]">
                                {(data?.daily_views || []).length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={data.daily_views}>
                                            <defs><linearGradient id="viewGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D4AF37" stopOpacity={0.3} /><stop offset="100%" stopColor="#D4AF37" stopOpacity={0} /></linearGradient></defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#1e2636" />
                                            <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickFormatter={v => v.slice(5)} />
                                            <YAxis stroke="#64748b" fontSize={10} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Area type="monotone" dataKey="views" name="Views" stroke="#D4AF37" fill="url(#viewGrad)" strokeWidth={2} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                ) : <div className="flex items-center justify-center h-full text-white/20 text-sm">No data yet — pageviews will appear as visitors browse</div>}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader className="pb-2"><CardTitle className="text-white font-outfit text-base">Devices</CardTitle></CardHeader>
                        <CardContent>
                            {(data?.devices || []).length > 0 ? (
                                <>
                                    <div className="h-[160px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart><Pie data={data.devices.map(d => ({ name: d.device, value: d.count }))} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                                                {data.devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                            </Pie><Tooltip content={<CustomTooltip />} /></PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="space-y-2 mt-2">
                                        {data.devices.map((d, i) => { const Icon = DEVICE_ICONS[d.device] || Monitor; return (
                                            <div key={d.device} className="flex items-center justify-between text-sm">
                                                <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i] }} /><Icon className="w-3.5 h-3.5 text-white/30" /><span className="text-white/50 text-xs capitalize">{d.device}</span></div>
                                                <span className="text-white font-medium text-xs">{d.count}</span>
                                            </div>); })}
                                    </div>
                                </>
                            ) : <div className="flex items-center justify-center h-[200px] text-white/20 text-sm">No data yet</div>}
                        </CardContent>
                    </Card>
                </div>

                {/* Charts Row 2 */}
                <div className="grid lg:grid-cols-2 gap-5">
                    {/* Top Countries */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader className="pb-2"><CardTitle className="text-white font-outfit text-base flex items-center gap-2"><Globe className="w-4 h-4 text-blue-400" /> Visitors by Country</CardTitle></CardHeader>
                        <CardContent>
                            {(data?.top_countries || []).length > 0 ? (
                                <div className="space-y-2.5">
                                    {data.top_countries.slice(0, 10).map((c, i) => {
                                        const max = data.top_countries[0]?.sessions || 1;
                                        return (
                                            <div key={c.country} className="flex items-center gap-3">
                                                <span className="text-xs text-white/30 w-5 text-right">{i + 1}</span>
                                                <div className="flex-1">
                                                    <div className="flex justify-between mb-1"><span className="text-xs text-white">{c.country}</span><span className="text-xs text-white/50">{c.sessions}</span></div>
                                                    <div className="h-1.5 rounded-full bg-white/5"><div className="h-full rounded-full bg-blue-400" style={{ width: `${(c.sessions / max) * 100}%` }} /></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : <div className="py-8 text-center text-white/20 text-sm">No country data yet</div>}
                        </CardContent>
                    </Card>

                    {/* Top Pages */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader className="pb-2"><CardTitle className="text-white font-outfit text-base flex items-center gap-2"><Activity className="w-4 h-4 text-gold" /> Most Visited Pages</CardTitle></CardHeader>
                        <CardContent>
                            {(data?.top_pages || []).length > 0 ? (
                                <div className="space-y-2">
                                    {data.top_pages.slice(0, 10).map((p, i) => (
                                        <div key={p.page} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.04]">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="text-[10px] text-gold font-bold w-4">{i + 1}</span>
                                                <div className="min-w-0"><p className="text-xs text-white truncate">{p.title || p.page}</p><p className="text-[10px] text-white/20 truncate">{p.page}</p></div>
                                            </div>
                                            <span className="text-xs text-white/50 shrink-0 ml-2">{p.views}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : <div className="py-8 text-center text-white/20 text-sm">No page data yet</div>}
                        </CardContent>
                    </Card>
                </div>

                {/* Row 3: Listings, Cities, Blogs, Events */}
                <div className="grid lg:grid-cols-2 gap-5">
                    {/* Top Listings */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader className="pb-2"><CardTitle className="text-white font-outfit text-base">Top Listings Viewed</CardTitle></CardHeader>
                        <CardContent>
                            {(data?.top_listings || []).length > 0 ? (
                                <div className="space-y-2">
                                    {data.top_listings.map((l, i) => (
                                        <div key={l.id} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02]">
                                            <div className="flex items-center gap-2"><span className="text-[10px] text-emerald-400 font-bold">{i + 1}</span><span className="text-xs text-white truncate">{l.title || l.id}</span></div>
                                            <span className="text-xs text-white/50">{l.views} views</span>
                                        </div>
                                    ))}
                                </div>
                            ) : <div className="py-6 text-center text-white/20 text-sm">No listing views yet</div>}
                        </CardContent>
                    </Card>

                    {/* Browsers + Referrers */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader className="pb-2"><CardTitle className="text-white font-outfit text-base">Browsers & Referrers</CardTitle></CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-[10px] text-white/30 uppercase mb-2">Browsers</p>
                                    {(data?.browsers || []).map((b, i) => (
                                        <div key={b.browser} className="flex items-center justify-between py-1">
                                            <span className="text-xs text-white/60 capitalize">{b.browser}</span>
                                            <span className="text-xs text-white/40">{b.count}</span>
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <p className="text-[10px] text-white/30 uppercase mb-2">Top Referrers</p>
                                    {(data?.top_referrers || []).length > 0 ? data.top_referrers.slice(0, 5).map(r => (
                                        <div key={r.referrer} className="flex items-center justify-between py-1">
                                            <span className="text-xs text-white/60 truncate pr-2">{r.referrer.replace(/https?:\/\//, '').split('/')[0]}</span>
                                            <span className="text-xs text-white/40">{r.count}</span>
                                        </div>
                                    )) : <p className="text-xs text-white/20">No referrer data</p>}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* User Flows */}
                {(data?.user_flows || []).length > 0 && (
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader className="pb-2"><CardTitle className="text-white font-outfit text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-gold" /> User Journeys (Top Paths)</CardTitle></CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                {data.user_flows.map((f, i) => (
                                    <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-white/[0.02]">
                                        <span className="text-[10px] text-gold font-bold w-4">{i + 1}</span>
                                        <div className="flex items-center gap-1.5 flex-wrap flex-1">
                                            {(f.path || []).map((page, j) => (
                                                <span key={j} className="flex items-center gap-1">
                                                    <span className="text-xs text-white/60 bg-white/5 px-2 py-0.5 rounded">{page === '/' ? 'Home' : page.replace(/\//g, ' / ').trim()}</span>
                                                    {j < f.path.length - 1 && <ArrowRight className="w-3 h-3 text-white/20" />}
                                                </span>
                                            ))}
                                        </div>
                                        <span className="text-xs text-white/40 shrink-0">{f.count}x</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Hourly Distribution */}
                {(data?.hourly_distribution || []).length > 0 && (
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader className="pb-2"><CardTitle className="text-white font-outfit text-base">Traffic by Hour of Day</CardTitle></CardHeader>
                        <CardContent>
                            <div className="h-[200px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={data.hourly_distribution}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#1e2636" />
                                        <XAxis dataKey="hour" stroke="#64748b" fontSize={10} tickFormatter={v => `${v}:00`} />
                                        <YAxis stroke="#64748b" fontSize={10} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="views" name="Views" fill="#D4AF37" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </DashboardLayout>
    );
}
